import api from './api';
import { Page } from './warehouse.service';

export type PaymentMode = 'cash' | 'bank_transfer' | 'cheque' | 'upi' | 'card' | 'other';

export interface PaymentAllocationRow {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  grandTotal: number;
  paidAmount: number;
  amount: number;
}

export interface Payment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  customerId: string;
  customerName: string | null;
  amount: number;
  allocatedAmount: number;
  // Received and not yet set against an invoice
  unallocatedAmount: number;
  mode: PaymentMode;
  referenceNumber: string | null;
  bankName?: string | null;
  notes?: string | null;
  status: 'posted' | 'cancelled';
  cancelReason?: string | null;
  allocations?: PaymentAllocationRow[];
  createdAt: string;
}

export interface OutstandingInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  customerId: string;
  customerName: string;
  grandTotal: number;
  paidAmount: number;
  balance: number;
  daysOverdue: number;
}

export interface AgeingRow {
  customerId: string;
  customerName: string | null;
  customerCode: string | null;
  invoices: number;
  notDue: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  total: number;
  onAccount: number;
}

export interface PaymentSummary {
  receipts: number;
  receivedThisMonth: number;
  onAccount: number;
  openInvoices: number;
  outstanding: number;
  overdueInvoices: number;
  overdue: number;
}

export interface PaymentInput {
  customerId: string;
  paymentDate?: string;
  amount: number;
  mode?: PaymentMode;
  referenceNumber?: string | null;
  bankName?: string | null;
  notes?: string | null;
  allocations?: { invoiceId: string; amount: number }[];
}

export interface PaymentQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'posted' | 'cancelled' | '';
  mode?: PaymentMode | '';
  customerId?: string;
  unallocated?: boolean;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));

export const paymentsService = {
  getPage: async (params: PaymentQueryParams): Promise<Page<Payment>> => (await api.get('/payments', { params: clean(params) })).data,
  getSummary: async (): Promise<PaymentSummary> => (await api.get('/payments/summary')).data,
  getOutstanding: async (params: { page?: number; limit?: number; search?: string; customerId?: string; overdueOnly?: boolean }): Promise<Page<OutstandingInvoice> & { totalBalance: number }> =>
    (await api.get('/payments/outstanding', { params: clean(params) })).data,
  getAgeing: async (): Promise<{ data: AgeingRow[]; totals: Record<string, number> }> => (await api.get('/payments/ageing')).data,
  getOne: async (id: string): Promise<Payment> => (await api.get(`/payments/${id}`)).data,
  create: async (data: PaymentInput): Promise<Payment> => (await api.post('/payments', data)).data,
  allocate: async (id: string, allocations: { invoiceId: string; amount: number }[]): Promise<Payment> =>
    (await api.post(`/payments/${id}/allocate`, { allocations })).data,
  cancel: async (id: string, reason: string): Promise<Payment> => (await api.post(`/payments/${id}/cancel`, { reason })).data,
};
